import { AppResp } from '@/types/api'
import {
  GameStats,
  SimulatorGameListData,
  SimulatorGameProject,
} from '@/types/simulatorGame'
import { MemberShipTier } from '@/types/subscribe'
import { BASE_API_URL } from '@/constant/api'
import { axiosAuth, axiosPublic } from '@/utils/axios'

export const STUDIO_API_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_API_BASE_URL

export const simulatorGameListUrl = `${BASE_API_URL}/game/public/projects`

export type SimulatorGameListReq = {
  language_code?: string
  nsfw: boolean
  page: number
  size: number
}

const DEFAULT_SIMULATOR_GAME_LIST_REQ: SimulatorGameListReq = {
  nsfw: false,
  page: 0,
  size: 20,
}

export const getSimulatorGameListKey = (
  req: Partial<SimulatorGameListReq> = {}
) => ({
  key: 'simulator-game-list',
  req: {
    ...DEFAULT_SIMULATOR_GAME_LIST_REQ,
    ...req,
  },
})

export const getSimulatorGameList = async (
  req: Partial<SimulatorGameListReq> = {}
) => {
  const request = {
    ...DEFAULT_SIMULATOR_GAME_LIST_REQ,
    ...req,
  }

  const res = await axiosPublic.post<AppResp<SimulatorGameListData>>(
    simulatorGameListUrl,
    request
  )

  if (!res.data.data) {
    throw new Error('No simulator game list data')
  }

  return res.data.data
}

export const getSimulatorGameDetailUrl = (
  projectId: string | number,
  languageCode?: string | null
) => `simulator-game-detail:${projectId}:${languageCode || 'en'}`

export const getSimulatorGameDetail = async (
  projectId: string | number,
  languageCode?: string | null
) => {
  const res = await axiosPublic.post<
    AppResp<{ projects: SimulatorGameProject[] }>
  >(simulatorGameListUrl, {
    game_ids: [projectId],
    language_code: languageCode || 'en',
  })

  const project = res.data.data?.projects?.[0]
  if (!project) {
    throw new Error('No simulator game detail data')
  }

  return project
}

const simulatorGameModelSettingGetUrl = `${BASE_API_URL}/user_project_setting/get`
const simulatorGameModelSettingSaveUrl = `${BASE_API_URL}/user_project_setting/save`

export type SimulatorGameChatModel = {
  text_model_id: string
  name: string
  description: string
  badge?: string
  temperature?: number
  temperature_range?: [number, number]
  content_diversity?: number
  repetition_penalty_range?: [number, number]
  temperature_limit?: number
  min_required_membership_tier?: MemberShipTier
  cost_gem_amount?: number
  min_cost_gem_amount?: number
  free_plan_ids?: number[] | null
  max_position_embeddings?: number
  context_len_price?: Record<string, Record<string, number>>
  relationship_multiplier?: number
  view_level?: number
  view_priority?: number
}

export type SimulatorGameModelSetting = {
  project_id?: string
  text_model_id: string
  context_length: number
  actual_memory_length?: number
  chat_models: SimulatorGameChatModel[]
}

export type SimulatorGameModelSettingReq = {
  project_id: string
  language_code: string
}

export type SimulatorGameModelSettingSaveReq = {
  project_id: string
  text_model_id: string
  context_length: number
}

export const getSimulatorGameModelSettingKey = (
  projectId?: string | null,
  languageCode?: string | null
) =>
  projectId
    ? {
        endpoint: simulatorGameModelSettingGetUrl,
        project_id: projectId,
        language_code: languageCode || 'en',
      }
    : null

export const getSimulatorGameModelSetting = async (
  req: SimulatorGameModelSettingReq
): Promise<SimulatorGameModelSetting> => {
  const res = await axiosAuth.post<AppResp<SimulatorGameModelSetting>>(
    simulatorGameModelSettingGetUrl,
    req
  )

  if (!res.data.data) {
    throw new Error('No simulator game model setting data')
  }

  return res.data.data
}

export const saveSimulatorGameModelSetting = async (
  req: SimulatorGameModelSettingSaveReq
) => {
  return axiosAuth.post<AppResp<unknown>>(simulatorGameModelSettingSaveUrl, req)
}

export type GamePlayReq = {
  game_id: string
  version?: number
}

export type GamePlayRes = {
  game_id: string
  play_count: number
  recorded: boolean
}

export const postGamePlay = async (req: GamePlayReq): Promise<GamePlayRes> => {
  const res = await axiosAuth.post<AppResp<GamePlayRes>>(
    `${BASE_API_URL}/game/play`,
    req
  )
  if (!res.data.data) {
    throw new Error('No game play data')
  }
  return res.data.data
}

export type GameStatsBatchRes = {
  items: Record<string, GameStats>
}

export const getGameStatsBatch = async (
  game_ids: string[]
): Promise<GameStatsBatchRes> => {
  const res = await axiosAuth.post<AppResp<GameStatsBatchRes>>(
    `${BASE_API_URL}/game/stats/batch`,
    { game_ids }
  )
  return res.data.data || { items: {} }
}

export const shareGame = async (game_id: string) =>
  axiosAuth.post<AppResp<{ game_id: string; share_count: number }>>(
    `${BASE_API_URL}/user/share/game`,
    { game_id }
  )

export type MemoryTokenCountRes = {
  memory_token_count: number
}

export const getMemoryTokenCountKey = (projectId?: string | null) =>
  projectId ? ['memory-token-count', projectId] : null

export const getMemoryTokenCount = async (
  projectId: string
): Promise<MemoryTokenCountRes> => {
  const res = await axiosAuth.get<AppResp<MemoryTokenCountRes>>(
    `${STUDIO_API_BASE_URL}/sdk/v1/chat/memory-token-count`,
    { params: { project_id: projectId } }
  )
  if (!res.data.data) {
    throw new Error('No memory token count data')
  }
  return res.data.data
}
